/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled/macro'
import { ActionButton } from 'components/widgets'
import {
  CONNECTIVITY_DISMISSED_KEY,
  ConnectivityTarget,
  CORE_TARGETS,
  ERROR_COLOR,
  GRAY3_COLOR,
  TITLE_COLOR,
  VOICE_VERDICT_GRACE_MS,
  voiceTargets,
} from 'lib/constants'
import { trans, TranslationGroup, TranslationKey } from 'lib/types'
import { isSuperAdmin, isVoiceEnabled } from 'lib/utils/auth'
import { hasVoiceLicenseForOrg } from 'lib/utils/licenses'
import { formatReport, previewResults, ProbeResult, runConnectivityChecks, unreachable } from 'models/api/connectivity'
import { useGlobal } from 'contexts/GlobalProvider'
import { useVoice } from 'contexts/VoiceProvider'

const Backdrop = styled.section({
  position: 'fixed',
  inset: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'rgba(0,0,0,.45)',
  // Above the app but below nothing else; this is the most important thing on
  // screen when it appears, because everything behind it is already degraded.
  zIndex: 2000,
  padding: '24px',
})

const Panel = styled.div({
  background: 'white',
  borderRadius: '10px',
  fontFamily: 'Montserrat',
  maxWidth: '640px',
  width: '100%',
  maxHeight: '100%',
  overflowY: 'auto',
  padding: '28px 32px',
})

const Title = styled.h2({
  color: ERROR_COLOR,
  fontSize: '1.9rem',
  fontWeight: 700,
  margin: '0 0 12px',
})

const Intro = styled.p({
  color: TITLE_COLOR,
  fontSize: '1.3rem',
  lineHeight: 1.5,
  margin: '0 0 20px',
})

// The instruction the operator acts on. Bold because everything above it is
// diagnosis and this is the only line that asks for something.
const Action = styled.strong({ fontWeight: 700 })

const Row = styled.li({
  background: GRAY3_COLOR,
  borderLeft: `4px solid ${ERROR_COLOR}`,
  borderRadius: '6px',
  listStyle: 'none',
  margin: '0 0 12px',
  padding: '12px 14px',
})

const RowHead = styled.div({
  alignItems: 'baseline',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginBottom: '6px',
})

const RowLabel = styled.span({ color: TITLE_COLOR, fontSize: '1.4rem', fontWeight: 700 })

const Tag = styled.span<{ tone: 'core' | 'voice' }>(props => ({
  background: props.tone === 'core' ? ERROR_COLOR : '#8a8a8a',
  borderRadius: '10px',
  color: 'white',
  fontSize: '1rem',
  fontWeight: 700,
  letterSpacing: '0.05rem',
  padding: '2px 8px',
  textTransform: 'uppercase',
}))

// The URL is the payload an IT team acts on, so it is selectable, wraps rather
// than truncates, and is never abbreviated.
const Url = styled.code({
  color: TITLE_COLOR,
  display: 'block',
  fontFamily: 'monospace',
  fontSize: '1.2rem',
  overflowWrap: 'anywhere',
  userSelect: 'all',
})

const Impact = styled.div({ color: TITLE_COLOR, fontSize: '1.2rem', marginTop: '6px', opacity: 0.85 })

const Actions = styled.div({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: '20px',
})

const Copied = styled.span({ color: TITLE_COLOR, fontSize: '1.2rem' })

/**
 * Names any host the portal could not reach, so an operator can hand the list
 * to their IT team.
 *
 * Mounted inside the authenticated providers: the checks only run for a
 * signed-in operator, and being under `GlobalProvider` is what lets the voice
 * probe be gated on the selected org and on this user's voice license.
 *
 * Deliberately dismissible. A probe can be wrong — a proxy that answers with
 * its own block page reads as reachable, and a slow link can time out while
 * being merely slow — so this must never be the thing that stops someone
 * getting to an active alert.
 */
const byId = (results: ProbeResult[]) =>
  results.reduce<{ [id: string]: ProbeResult }>((map, result) => ({ ...map, [result.target.id]: result }), {})

export const ConnectivityOverlay = () => {
  const translation: TranslationGroup = trans.group(TranslationKey.CONNECTIVITY)
  const { selectedOrg, currentUserV5, licenses, licensesLoading } = useGlobal()
  // The app's own Zello socket is a better connectivity signal than any probe:
  // it is the connection that has to work, and the transport already separates
  // a failed upgrade from a rejected logon. Mounted inside `VoiceProvider`
  // (see App.tsx), so this is available before any voice row is decided.
  const { zelloState, voiceReady, reconnect } = useVoice()

  // Design-time preview (`?connectivity-preview`): the panel is filled with
  // blocked rows and no probe is sent, so it can be iterated on from a network
  // where everything is reachable. Read once — changing the URL is a reload.
  const preview = useRef(previewResults()).current

  // Keyed by target id so the voice probe merges in beside the core results
  // instead of replacing them.
  const [resultsById, setResultsById] = useState<{ [id: string]: ProbeResult }>(() => byId(preview || []))
  const [checking, setChecking] = useState(false)
  const [copied, setCopied] = useState(false)
  // A preview ignores an earlier dismissal: it was asked for explicitly, and
  // silently rendering nothing would read as the preview being broken.
  const [dismissed, setDismissed] = useState(() => !preview && !!sessionStorage.getItem(CONNECTIVITY_DISMISSED_KEY))

  // Orgs whose voice probe has already run, so flipping between properties is
  // not a re-probe. A ref rather than state: nothing renders from it.
  const voiceChecked = useRef<Set<string>>(new Set())

  // The subdomain the current Zello row describes, so a property switch can
  // drop it rather than leave the previous property's URL on screen.
  const voiceRowSubdomain = useRef<string | undefined>(undefined)

  const run = useCallback(async (targets: ConnectivityTarget[]) => {
    if (!targets.length) return
    setChecking(true)
    try {
      const probed = await runConnectivityChecks(targets)
      setResultsById(previous => {
        const next = { ...previous }
        probed.forEach(result => (next[result.target.id] = result))
        // Logged on every run so support can recover the report from a console
        // capture, even when the operator dismissed the panel unobserved.
        console.info(formatReport(Object.values(next)))
        return next
      })
    } finally {
      setChecking(false)
    }
  }, [])

  useEffect(() => {
    if (preview) {
      console.warn('Connectivity overlay is in preview mode. No hosts were probed; these results are fabricated.')
      return
    }
    run(CORE_TARGETS)
  }, [run, preview])

  // Zello is probed only for an operator who actually connects to it: the
  // property must have push-to-talk on, *and* this user must hold a voice
  // license for it. Same predicate `VoiceProvider` uses to decide whether voice
  // works at all, so the probe cannot accuse a host the user never touches.
  //
  // This excludes a super admin without a license, who can only view channels,
  // and any user with no license, who never opens a socket. Both would
  // otherwise send an IT team after zellowork.io for nothing.
  // A super admin views channels but never holds a voice session, so
  // zellowork.io is never a host they touch — excluded outright, ahead of the
  // license check. Note this is stricter than `VoiceProvider`, which blocks
  // voice actions only for a super admin *without* a license; drop the
  // `!isSuperAdmin()` term if a licensed super admin should be probed.
  const voiceLicensed =
    !isSuperAdmin() &&
    hasVoiceLicenseForOrg({
      userId: currentUserV5?.id,
      orgId: selectedOrg?.id,
      licenses,
    })

  const subdomain = selectedOrg?.zelloSubdomain

  // Adds `subdomain` to the gating above: without one there is no URL to test
  // and no socket for the app to open, so there is nothing to report either
  // way. A half-loaded license list is excluded too — deciding on it would
  // probe, or prune, on data that is about to change.
  const voiceEligible = Boolean(
    selectedOrg?.id && !licensesLoading && isVoiceEnabled(selectedOrg) && voiceLicensed && subdomain,
  )

  const voiceTarget = useMemo(() => voiceTargets(subdomain)[0], [subdomain])

  /**
   * What the app's own socket proves about reaching zellowork.io, or `null`
   * while it proves nothing yet.
   *
   * `ws_open_failed` is the only event the transport can emit *before* the
   * socket is open, so it is the only one that means the network is at fault.
   * Every other event — a rejected logon, a close, a kick, a channel or mic
   * problem — can only happen once the handshake succeeded, and therefore
   * proves the host is reachable. Reading it as an allowlist of one keeps that
   * true as the transport grows new events: a new failure mode reads as
   * reachable rather than silently accusing an operator's firewall.
   *
   * The one case this under-reports is a blocked reconnect triggered from the
   * TX path, where `reconnect_failed` lands after `ws_open_failed` and hides
   * it. That direction is the safe one — a missed warning, not a false accusation.
   */
  const liveVoiceOutcome = useMemo(() => {
    if (!zelloState.lastEvent) return null
    return zelloState.lastEvent === 'ws_open_failed'
      ? { reachable: false, detail: 'could not connect' }
      : { reachable: true, detail: '' }
  }, [zelloState.lastEvent])

  useEffect(() => {
    if (preview) return
    if (licensesLoading) return

    if (!voiceEligible) {
      // Drop any Zello result carried in from a property where they *were*
      // licensed, so switching properties clears it from the panel and from the
      // copied report rather than leaving a row they cannot act on. Returning
      // `previous` unchanged keeps this from re-rendering on every pass.
      setResultsById(previous => {
        if (!Object.values(previous).some(result => result.target.group === 'voice')) return previous
        const next: { [id: string]: ProbeResult } = {}
        Object.entries(previous).forEach(([id, result]) => {
          if (result.target.group !== 'voice') next[id] = result
        })
        return next
      })
      // Cleared, not narrowed: coming back to a property that was probed should
      // probe it again, since its row was just discarded.
      voiceChecked.current.clear()
      return
    }

    // A property switch invalidates the row: its URL and its verdict both
    // belong to the property just left. Dropped rather than shown stale, and
    // the transport clears `lastEvent` on the fresh connect so no verdict is
    // written again until this property's own attempt reports.
    if (voiceRowSubdomain.current !== subdomain) {
      voiceRowSubdomain.current = subdomain
      setResultsById(previous => {
        if (!Object.values(previous).some(result => result.target.group === 'voice')) return previous
        const next: { [id: string]: ProbeResult } = {}
        Object.entries(previous).forEach(([id, result]) => {
          if (result.target.group !== 'voice') next[id] = result
        })
        return next
      })
    }

    // The live socket settled it. Written only when the verdict actually
    // changes, so a re-render of `zelloState` does not loop through here.
    if (!voiceTarget || !liveVoiceOutcome) return
    setResultsById(previous => {
      const existing = previous[voiceTarget.id]
      if (
        existing &&
        existing.reachable === liveVoiceOutcome.reachable &&
        existing.detail === liveVoiceOutcome.detail
      ) {
        return previous
      }
      return { ...previous, [voiceTarget.id]: { target: voiceTarget, ...liveVoiceOutcome } }
    })
  }, [preview, licensesLoading, voiceEligible, voiceTarget, liveVoiceOutcome, subdomain])

  // Fallback probe, for the cases where there will be no live socket to read.
  // Two of them: this user has no Zello credentials, so `VoiceProvider` never
  // attempts a connection at all (`voiceReady` false); or an attempt was made
  // and never settled. Probing is then the only way an operator behind a
  // firewall hears about it. At most once per property.
  useEffect(() => {
    if (preview || !voiceEligible || !voiceTarget || liveVoiceOutcome) return

    const orgId = String(selectedOrg?.id)
    if (voiceChecked.current.has(orgId)) return

    // With no attempt coming, probe straight away. With one in flight, let it
    // answer first — Zello Work allows a single session per user, and while
    // this probe closes before sending `logon`, not opening a second socket at
    // all is better still.
    const timer = setTimeout(
      () => {
        voiceChecked.current.add(orgId)
        run([voiceTarget])
      },
      voiceReady ? VOICE_VERDICT_GRACE_MS : 0,
    )
    return () => clearTimeout(timer)
  }, [preview, voiceEligible, voiceTarget, liveVoiceOutcome, voiceReady, selectedOrg, run])

  const results = Object.values(resultsById)
  const blocked = unreachable(results)

  // Re-probe everything probed so far, so a retry on a voice org does not
  // silently narrow the report back down to the core hosts.
  const retry = () => {
    setCopied(false)
    // In a preview there is nothing to re-probe, so hold the checking state
    // briefly instead: the in-flight look is part of what needs reviewing.
    if (preview) {
      setChecking(true)
      setTimeout(() => setChecking(false), 1200)
      return
    }
    // The voice row is re-tested through whichever mechanism owns it. Where the
    // live socket reported it, the honest retry is to make the app connect
    // again rather than to open a probe socket beside it; where the fallback
    // probe reported it, re-probe. Core hosts are always re-probed, so a retry
    // on a voice org does not silently narrow the report back down to them.
    if (liveVoiceOutcome && voiceEligible) {
      reconnect()
      run(CORE_TARGETS)
      return
    }
    run([...CORE_TARGETS, ...(voiceEligible && voiceTarget ? [voiceTarget] : [])])
  }

  const dismiss = () => {
    sessionStorage.setItem(CONNECTIVITY_DISMISSED_KEY, '1')
    setDismissed(true)
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(formatReport(results))
      setCopied(true)
    } catch {
      // Clipboard access needs a secure context and permission. The URLs are
      // already on screen and selectable, so failing here costs convenience.
      setCopied(false)
    }
  }

  if (dismissed || !blocked.length) return null

  return (
    <Backdrop role="alertdialog" aria-labelledby="connectivity-title">
      <Panel>
        <Title id="connectivity-title">{translation.title}</Title>
        <Intro>
          {translation.intro} <Action>{translation.intro_action}</Action>
        </Intro>

        <ul css={{ margin: 0, padding: 0 }}>
          {blocked.map(({ target, detail }) => (
            <Row key={target.id}>
              <RowHead>
                <RowLabel>{target.label}</RowLabel>
                <Tag tone={target.group}>{target.group === 'core' ? translation.required : translation.voice}</Tag>
                <span css={{ color: ERROR_COLOR, fontSize: '1.2rem' }}>
                  {translation.blocked}
                  {detail ? ` — ${detail}` : ''}
                </span>
              </RowHead>
              <Url>{target.url}</Url>
              <Impact>
                {translation.impact_label}: {target.impact}
              </Impact>
            </Row>
          ))}
        </ul>

        <Actions>
          <ActionButton height="28px" onClick={retry} loading={checking} disabled={checking}>
            {checking ? translation.checking : translation.retry}
          </ActionButton>
          <ActionButton height="28px" onClick={copy} disabled={checking}>
            {translation.copy}
          </ActionButton>
          <ActionButton height="28px" onClick={dismiss}>
            {translation.dismiss}
          </ActionButton>
          {copied && <Copied>{translation.copied}</Copied>}
        </Actions>
      </Panel>
    </Backdrop>
  )
}

export default ConnectivityOverlay
